import { AnalysisRun, AnalysisRunService } from "./analysis-run.service";
import { Injectable } from "@angular/core";
import { RiskAssessment } from "../models/risk-assessment";
import { RiskLevel } from "../models/risk-level";
import {
  CacheNamesEnum,
  IMultipleConditions,
  NodeResult,
  OverviewInfo,
  OverviewMapValue,
  OverviewSectionsDTO,
  OverviewSectionsEnum
} from "../models/shared.model";
import { Flow } from "../models/flow";
import { IEvaluator } from "../interfaces/evaluator-interface";
import { LogService } from "./log.service";
import { WaitService } from "./wait.service";


@Injectable()
export class RiskAssessmentService {
  public assessment: RiskAssessment = new RiskAssessment(RiskLevel.High);
  public weightedResult: number = 0;
  public evaluatorsScoreMap: Map<string, number>;
  public overviewMap: Map<string, OverviewMapValue> = new Map();

  private overviewSectionsDto: OverviewSectionsDTO[];

  constructor(
    private logService: LogService,
    private runs: AnalysisRunService,
    private waitService: WaitService
  ) {
    this.weightedResult = 0;
    this.evaluatorsScoreMap = new Map<string, number>();

    this.waitService.dataLoadedChange.subscribe((changed) => {
      if (changed) {
        this.overviewSectionsDto = this.getOverviewSections();

        (this.overviewSectionsDto || []).forEach((section) => {
          this.overviewMap.set(section.name, {displayOrder: 0, overviewTextSet: new OverviewTextSet()});
        });
      }
    });

    this.waitService.resetData.subscribe((reset) => {
      if (reset) {
        this.resetOverviewMap();
        this.weightedResult = 0;
        this.evaluatorsScoreMap = new Map<string, number>();
        this.assessment = new RiskAssessment(RiskLevel.High);
        this.overviewMap = new Map();
      }
    });
  }

  getOverviewSections(): OverviewSectionsDTO[] {
    let list = JSON.parse(window.localStorage.getItem(CacheNamesEnum.OVERVIEW_SECTIONS)!) as OverviewSectionsDTO[];

    if (!list || list.length == 0) {
      list = [
        {
          id: 1,
          name: OverviewSectionsEnum.REASONS_CONC,
          display_order: 1,
          sorting_criteria: 'desc',
          icon_color: '#d41616',
          item_limit: 6
        },
        {
          id: 2,
          name: OverviewSectionsEnum.LESS_SUS,
          display_order: 2,
          sorting_criteria: 'asc',
          icon_color: '#40b592',
          item_limit: 6
        },
        {
          id: 3,
          name: OverviewSectionsEnum.GOOD_SIGNS,
          display_order: 3,
          sorting_criteria: 'asc',
          icon_color: '#198de6',
          item_limit: 6
        }
      ]
    }

    return list;
  }

  async performAnalysis(evaluator: IEvaluator, run: AnalysisRun = this.runs.current): Promise<RiskAssessment> {
    run.assertActive();
    this.weightedResult = 0;

    return evaluator.runAnalysis().then((result) => {
      run.assertActive();

      const type = evaluator.getType();


      this.logService.appendEvaluatorLogs([...evaluator.getLogs()]);

      result.weight = this.setScoring(evaluator, type);

      this.assessment.addAnalysisResult(result);


      return this.assessment;
    });
  }

  public setScoring(evaluator: IEvaluator, type: string, forceSetScore: boolean = false) {
    let score = 0;

    Object.values(evaluator.getFlow().nodes).forEach(flow => {
      if (flow.result)
        score += flow.result.points;
    });

    let currentScore = this.evaluatorsScoreMap.get(type);

    if ((currentScore < score) || forceSetScore) {
      this.evaluatorsScoreMap.set(type, score);
    }

    return score;
  }

  public resetOverviewMap() {
    (this.overviewSectionsDto || []).forEach((section) => {
      this.overviewMap.set(section.name, {displayOrder: 0, overviewTextSet: new OverviewTextSet()});
    });
  }

  public setOverviewMap(flow: Flow): void {
    if (!flow) return;

    Object.values(flow.nodes).forEach(node => {
      const overview = node.result?.overviewText;
      if (!overview) return;

      this.addToOverviewMap(node.result, overview);
    });
  }

  public setMultipleConditionsOverview(multipleConditions: IMultipleConditions[]){
    multipleConditions.forEach((item) => {
      if (item.result && item.result.decision == "true" && item.result.overviewText) {
       this.addToOverviewMap(item.result, item.result.overviewText, true);
      }
    });
  }

  addToOverviewMap(nodeResult: NodeResult, overview: OverviewInfo, isMultipleCondition: boolean = false){
    const overviewSection = this.overviewSectionsDto.find((d) => d.name === overview.section);
    const mapKey = overviewSection?.name;
    if (!mapKey) return;

    overview.isMultipleCondition = isMultipleCondition;

    const currentValues = this.overviewMap.get(mapKey).overviewTextSet ?? new OverviewTextSet([]);
    const updatedValues = [...currentValues, nodeResult];
    this.sortOverviewTextSet(updatedValues, overview.section);
    this.overviewMap.set(mapKey, {
      displayOrder: overviewSection.display_order,
      overviewTextSet: new OverviewTextSet(updatedValues)
    });
  }

  private sortOverviewTextSet(textSet: NodeResult[], section: string): void {
    const sectionDTO = this.overviewSectionsDto.find((d) => d.name === section);

    const sortByPoints =
      (a: NodeResult, b: NodeResult) => sectionDTO.sorting_criteria == 'desc' ? b.points - a.points : a.points - b.points;
    textSet.sort(sortByPoints);
  }

  sortOverviewMap(overviewMap: Map<string, OverviewMapValue>): Map<string, OverviewMapValue> {
    // Convert Map to an array of entries [key, value]
    const sortedEntries = Array.from(overviewMap.entries()).sort(
      ([, valueA], [, valueB]) => valueA.displayOrder - valueB.displayOrder
    );

    // Convert sorted entries back to a Map
    return new Map(sortedEntries);
  }

  getOverviewMap() {
    return this.sortOverviewMap(this.overviewMap);
  }
}

export class OverviewTextSet extends Set<NodeResult> {

  add(object: NodeResult): this {
    if (!this.has(object)) {
      super.add(object);
    }
    return this;
  }

  has(value: NodeResult): boolean {

    for (let item of this) {
      if (item.overviewText.value === value.overviewText.value && item.points === value.points) {
        return true;
      }
    }
    return false;
  }
}
